import React, { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Bell,
  CheckCircle,
  Clock,
  Settings,
  Trash2,
  X,
  FileText,
  AlertTriangle,
  MessageSquare,
  Briefcase,
  Calendar,
  Info,
  Star,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { NotificationPreferences } from "./NotificationPreferences";

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ConvexNotification {
  _id: Id<"notifications">;
  type: string;
  title: string;
  message: string;
  orgId: Id<"organizations">;
  userId?: Id<"users">;
  relatedId?: string;
  relatedType?: string;
  opportunityId?: string;
  isRead: boolean;
  createdAt: number;
}

export function NotificationsPanel({ isOpen, onClose }: NotificationsPanelProps) {
  const navigate = useNavigate();
  const [showPreferences, setShowPreferences] = useState(false);

  // Get notifications from Convex
  const notifications = useQuery(api.notifications.getNotifications, {
    limit: 50,
  }) as ConvexNotification[] | undefined;

  const unreadCount = useQuery(api.notifications.getUnreadNotificationCount);

  // Mutations
  const markAsRead = useMutation(api.notifications.markNotificationAsRead);
  const markAllAsRead = useMutation(api.notifications.markAllNotificationsAsRead);

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Failed to mark all as read:", error);
      toast.error("Failed to mark notifications as read");
    }
  };

  const handleMarkAsRead = async (notificationId: Id<"notifications">) => {
    try {
      await markAsRead({ notificationId });
    } catch (error) {
      console.error("Failed to mark as read:", error);
      toast.error("Failed to mark notification as read");
    }
  };

  const handleNotificationClick = async (notification: ConvexNotification) => {
    // Mark as read first
    await handleMarkAsRead(notification._id);

    // Navigate based on notification type and related data
    if (notification.relatedId && notification.relatedType) {
      switch (notification.type) {
        case "message_received":
          // Navigate to messages page with conversation pre-selected
          if (notification.relatedType === "conversations") {
            navigate(`/messages?conversation=${notification.relatedId}`);
          }
          break;

        case "conversation_started":
          // Navigate to messages page with conversation pre-selected
          if (notification.relatedType === "conversations") {
            navigate(`/messages?conversation=${notification.relatedId}`);
          }
          break;

        case "review_received":
          // Navigate to reviews page to see the new review
          navigate("/reviews");
          break;

        case "bid_status_change":
          // Navigate to the bid detail page
          navigate(`/bids/${notification.relatedId}`);
          break;

        default:
          // For other notification types, just mark as read without navigation
          break;
      }
    }

    // Close the notifications panel after navigation
    onClose();
  };

  const formatNotificationTime = (timestamp: number) => {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "application_status_change":
        return <FileText className="h-5 w-5 text-blue-500" />;
      case "new_application":
        return <Briefcase className="h-5 w-5 text-green-500" />;
      case "application_submitted":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "new_opportunity":
        return <Briefcase className="h-5 w-5 text-purple-500" />;
      case "deadline_reminder":
        return <Clock className="h-5 w-5 text-orange-500" />;
      case "document_expiring":
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case "system_announcement":
        return <Info className="h-5 w-5 text-blue-500" />;
      case "message_received":
        return <MessageSquare className="h-5 w-5 text-blue-500" />;
      case "conversation_started":
        return <MessageSquare className="h-5 w-5 text-green-500" />;
      case "review_received":
        return <Star className="h-5 w-5 text-yellow-500" />;
      case "bid_status_change":
        return <FileText className="h-5 w-5 text-indigo-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  const getNotificationTypeColor = (type: string) => {
    switch (type) {
      case "application_status_change":
        return "text-blue-600";
      case "new_application":
        return "text-green-600";
      case "application_submitted":
        return "text-green-600";
      case "new_opportunity":
        return "text-purple-600";
      case "deadline_reminder":
        return "text-orange-600";
      case "document_expiring":
        return "text-yellow-600";
      case "system_announcement":
        return "text-blue-600";
      case "message_received":
        return "text-blue-600";
      case "bid_status_change":
        return "text-indigo-600";
      default:
        return "text-gray-600";
    }
  };

  if (notifications === undefined || unreadCount === undefined) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="w-[480px] p-0 sm:max-w-[480px]">
          <div className="flex h-64 items-center justify-center">
            <div className="text-center">
              <Bell className="mx-auto mb-4 h-12 w-12 text-gray-300" />
              <p className="text-gray-500">Loading notifications...</p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="w-[480px] p-0 sm:max-w-[480px]">
          <SheetHeader className="border-b p-6">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-xl">Notifications</SheetTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  disabled={unreadCount === 0}
                >
                  Mark all as read
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowPreferences(true)}>
                  <Settings className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {unreadCount > 0 && (
              <Badge variant="secondary" className="mt-2">
                {unreadCount} unread {unreadCount === 1 ? "notification" : "notifications"}
              </Badge>
            )}
          </SheetHeader>

          <ScrollArea className="h-[calc(100vh-100px)]">
            {notifications.length === 0 ? (
              <div className="flex h-[300px] flex-col items-center justify-center text-gray-500">
                <Bell className="mb-4 h-12 w-12 text-gray-300" />
                <p className="text-center">No notifications</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <div
                    key={notification._id}
                    className={`cursor-pointer p-4 transition-colors hover:bg-gray-50 ${!notification.isRead ? "bg-blue-50" : ""}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex gap-3">
                      <div className="mt-1 flex-shrink-0">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between">
                          <p
                            className={`text-sm font-medium ${!notification.isRead ? "text-blue-800" : "text-gray-900"}`}
                          >
                            {notification.title}
                          </p>
                          <span className="ml-2 whitespace-nowrap text-xs text-gray-500">
                            {formatNotificationTime(notification.createdAt)}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-600">{notification.message}</p>
                        {!notification.isRead && (
                          <div className="mt-2 flex items-center">
                            <div className="mr-2 h-2 w-2 rounded-full bg-blue-500"></div>
                            <span className="text-xs font-medium text-blue-600">New</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          <div className="border-t bg-gray-50 p-4">
            <Button variant="outline" className="w-full" onClick={onClose}>
              Close
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Notification Preferences Modal */}
      <NotificationPreferences isOpen={showPreferences} onClose={() => setShowPreferences(false)} />
    </>
  );
}
